import { useEffect, useRef } from 'react'
import { DownloadCta } from '../components/DownloadCta'
import { StickyDownloadBar } from '../components/StickyDownloadBar'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { trackWeb, WEB_EVENTS } from '../lib/analytics'
import { useLandingHeroVariant } from '../lib/experiments'
import { DEFAULT_LANDING_HERO_VARIANT, LANDING_HERO_VARIANTS } from './landing-hero-variants'
import './landing.css'

// Rotating phrases live in the variant map (hero) so copy has ONE home; the two
// in-body rotations below are variant-independent.
const NO_ROTATION: string[] = []

export default function Landing() {
  const rootRef = useRef<HTMLDivElement>(null)
  const reduced = usePrefersReducedMotion()
  const { variant, resolved } = useLandingHeroVariant()
  // While the flag is unresolved the hero renders the DEFAULT variant's copy but
  // invisibly, so there is no flash of the wrong headline. Which variant stands
  // in does not matter to layout: .stage is bottom-anchored inside a fixed-height
  // hero, so the CTA under the copy holds its position whatever height the
  // resolved copy turns out to be, and the copy itself only ever appears once.
  const hero = LANDING_HERO_VARIANTS[resolved ? variant : DEFAULT_LANDING_HERO_VARIANT]
  const heroPhrases = resolved && hero.rotate ? hero.lead : NO_ROTATION

  // ---- scroll reveals ----
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const els = root.querySelectorAll('.rv, .rv-wipe, .rv-stagger')
    if (reduced) {
      els.forEach((e) => e.classList.add('in'))
      return
    }
    if (!('IntersectionObserver' in window)) {
      els.forEach((e) => e.classList.add('in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in')
            io.unobserve(en.target)
          }
        })
      },
      { threshold: 0, rootMargin: '0px 0px -12% 0px' }
    )
    els.forEach((e) => io.observe(e))
    return () => io.disconnect()
  }, [reduced])

  // ---- section-progression funnel (how far down the page visitors get) ----
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (!('IntersectionObserver' in window)) return
    const sections: Array<[string, string]> = [
      ['.opener', 'opener'],
      ['.actsec', 'companion'],
      ['.threeup-sec', 'screens'],
      ['.benefits', 'concierge'],
      ['.wedge', 'closing'],
    ]
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const name = en.target.getAttribute('data-sv')
            if (name) trackWeb(WEB_EVENTS.WEB_LANDING_SECTION_VIEWED, { section: name })
            io.unobserve(en.target)
          }
        })
      },
      { threshold: 0, rootMargin: '0px 0px -20% 0px' }
    )
    sections.forEach(([sel, name]) => {
      const el = root.querySelector(sel)
      if (el) {
        el.setAttribute('data-sv', name)
        io.observe(el)
      }
    })
    return () => io.disconnect()
  }, [])

  // ---- text-states swap (hero + experience word) ----
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (reduced) return
    const phraseMap: Record<string, string[]> = {
      // Empty for a non-rotating hero variant (and until the flag resolves), so
      // no interval is ever armed for copy that is not meant to cycle.
      hero: heroPhrases,
      chaos: ['chaos', 'anxiety', 'FOMO', 'dread', 'overwhelm'],
      exp: ['experience', 'memory', 'story', 'feeling', 'taste'],
    }
    const cadence: Record<string, number> = { hero: 3600, chaos: 4200, exp: 4800 }
    const intervals: number[] = []
    const timeouts: number[] = []
    root.querySelectorAll<HTMLElement>('.hRot .word').forEach((el) => {
      const key = el.getAttribute('data-rot')
      if (!key) return
      const phrases = phraseMap[key]
      if (!phrases || phrases.length < 2) return
      let i = 0
      const id = window.setInterval(() => {
        el.classList.add('out')
        const tid = window.setTimeout(() => {
          i = (i + 1) % phrases.length
          el.textContent = phrases[i]
          el.classList.remove('out')
        }, 460)
        timeouts.push(tid)
      }, cadence[key] || 3600)
      intervals.push(id)
    })
    return () => {
      intervals.forEach((id) => window.clearInterval(id))
      timeouts.forEach((id) => window.clearTimeout(id))
    }
  }, [reduced, heroPhrases])

  // ---- Compass-card reveal (restrained: only the brass reply lands in) ----
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const cards = root.querySelectorAll('.cReveal')
    if (reduced) {
      cards.forEach((e) => e.classList.add('in'))
      return
    }
    if (!cards.length) return
    if (!('IntersectionObserver' in window)) {
      cards.forEach((e) => e.classList.add('in'))
      return
    }
    const timeouts: number[] = []
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const tid = window.setTimeout(() => {
              en.target.classList.add('in')
            }, 520)
            timeouts.push(tid)
            io.unobserve(en.target)
          }
        })
      },
      { threshold: 0.4, rootMargin: '0px 0px -8% 0px' }
    )
    cards.forEach((e) => io.observe(e))
    return () => {
      io.disconnect()
      timeouts.forEach((id) => window.clearTimeout(id))
    }
  }, [reduced])

  return (
    <div className='lp' ref={rootRef}>
      {/* ============== HERO ============== */}
      <section className='hero'>
        {/* LCP element: self-hosted + responsive so it is not gated on a
            third-party connection, and preloaded from index.html (which must
            keep the same webp srcset AND the same `sizes` string or the preload
            is wasted).
            `sizes` is deliberately 250vw in portrait: the hero box is 100svh
            with object-fit:cover and the plates are 5:4, so on a portrait phone
            the image is cover-scaled to roughly 2.7x the viewport WIDTH. Plain
            100vw under-requests and the browser upscales the picked source. */}
        <picture>
          <source
            type='image/webp'
            srcSet='/hero/hero-800.webp 800w, /hero/hero-1200.webp 1200w, /hero/hero-2000.webp 2000w'
            sizes='(orientation: portrait) 250vw, 100vw'
          />
          <img
            src='/hero/hero-1200.jpg'
            srcSet='/hero/hero-800.jpg 800w, /hero/hero-1200.jpg 1200w, /hero/hero-2000.jpg 2000w'
            sizes='(orientation: portrait) 250vw, 100vw'
            alt=''
            fetchPriority='high'
            decoding='async'
          />
        </picture>
        <div className='grad'></div>
        <nav className='nav'>
          <a href='#' className='wordmark'>
            Compass
          </a>
          <a href='#get' className='request'>
            Download
          </a>
        </nav>
        <div className='stage'>
          {/* Copy only: the CTA below is variant-independent and renders
              immediately, so a slow flag can never hide the conversion path. */}
          <div className={resolved ? 'heroCopy' : 'heroCopy pending'}>
            <h1>
              <span className='hRot'>
                <span className='word' data-rot={hero.rotate ? 'hero' : undefined}>
                  {hero.lead[0]}
                </span>
              </span>
              {hero.tail ? (
                <>
                  <br />
                  {hero.tail.before}
                  <span className='brass'>{hero.tail.brass}</span>
                  {hero.tail.after}
                </>
              ) : null}
            </h1>
            <div className='sub'>{hero.sub}</div>
          </div>
          <div className='stores' id='get'>
            <DownloadCta placement='hero' />
          </div>
        </div>
        <div className='scrollHint'>
          <span>Scroll</span>
          <span className='line'></span>
        </div>
      </section>

      {/* ============== OPENING: identity -> collecting -> pain -> chaos ============== */}
      <section className='opener' id='how'>
        <div className='coldopen'>
          <p className='line rv-wipe'>
            You've never just wanted a trip. You've always wanted the one that feels like{' '}
            <span className='brass'>you</span>.
          </p>
        </div>

        <div className='collect'>
          <h2 className='rv-wipe'>So you've been collecting it for months.</h2>
          <div className='exprail rv-stagger'>
            <div className='exp'>
              <div className='ph'>
                <img
                  src='https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=560&q=80'
                  alt=''
                  loading='lazy'
                />
              </div>
              <div className='lbl'>The Coldplay show that's been on your bucket list.</div>
            </div>
            <div className='exp'>
              <div className='ph'>
                <img
                  src='https://images.unsplash.com/photo-1483347756197-71ef80e95f73?auto=format&fit=crop&w=560&q=80'
                  alt=''
                  loading='lazy'
                />
              </div>
              <div className='lbl'>The aurora road trip a friend keeps raving about.</div>
            </div>
            <div className='exp'>
              <div className='ph'>
                <img
                  src='https://images.unsplash.com/photo-1490806843957-31f4c9a91c65?auto=format&fit=crop&w=560&q=80'
                  alt=''
                  loading='lazy'
                />
              </div>
              <div className='lbl'>The cherry blossoms all over your feed.</div>
            </div>
            <div className='exp'>
              <div className='ph'>
                <img
                  src='https://images.unsplash.com/photo-1502680390469-be75c86b636f?auto=format&fit=crop&w=560&q=80'
                  alt=''
                  loading='lazy'
                />
              </div>
              <div className='lbl'>The surf break you've saved a dozen reels of.</div>
            </div>
          </div>
        </div>

        <div className='painturn'>
          <p className='turn rv-wipe'>
            Then a long weekend lands, and turning it all into one plan becomes its own{' '}
            <span className='brass hRot'>
              <span className='word' data-rot='chaos'>
                chaos
              </span>
            </span>
            .
          </p>
          <p className='paindetail rv'>
            Too many choices, no one agrees - and the version that's actually yours costs more than
            the month allows.
          </p>
        </div>

        <div className='phones rv-stagger'>
          <div className='painCol'>
            <div className='sph tl'>
              <div className='scr'>
                <div className='wa'>
                  <div className='top'>
                    <svg className='icn bk' viewBox='0 0 24 24'>
                      <path d='M15 18l-6-6 6-6' />
                    </svg>
                    <div className='av'>SL</div>
                    <div className='nm'>
                      Sri Lanka 2026<small>6 members</small>
                    </div>
                  </div>
                  <div className='body'>
                    <div className='msg rx'>
                      <span className='who' style={{ color: '#d9527e' }}>
                        Priya
                      </span>
                      Ella or just the south?? we keep flip-flopping 😩
                    </div>
                    <div className='msg rx'>
                      <span className='who' style={{ color: '#1f9e6b' }}>
                        Rohan
                      </span>
                      south coast. Mirissa for whales{' '}
                      <span className='lnk'>tripadvisor.com/mirissa</span>
                    </div>
                    <div className='msg tx'>
                      saw a reel of Galle Fort, adding it<span className='tm'>9:42</span>
                    </div>
                    <div className='poll'>
                      <div className='q'>VOTE: how many nights?</div>
                      <div className='opt'>
                        <span>4 nights</span>
                        <span>3</span>
                      </div>
                      <div className='opt'>
                        <span>6 nights</span>
                        <span>2</span>
                      </div>
                    </div>
                    <div className='msg rx'>
                      <span className='who' style={{ color: '#d9821a' }}>
                        Karan
                      </span>
                      someone just MAKE a plan 🙏
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='painCap'>
              <div className='e'>The group chat</div>
              <div className='t'>Goes in circles for weeks.</div>
            </div>
          </div>

          <div className='painCol'>
            <div className='sph'>
              <div className='scr'>
                <div className='ig'>
                  <div className='top'>
                    <svg className='icn bk' viewBox='0 0 24 24'>
                      <path d='M15 18l-6-6 6-6' />
                    </svg>
                    <div className='nm'>Sri Lanka</div>
                    <svg className='icn mn' viewBox='0 0 24 24'>
                      <circle cx='5' cy='12' r='1.3' />
                      <circle cx='12' cy='12' r='1.3' />
                      <circle cx='19' cy='12' r='1.3' />
                    </svg>
                  </div>
                  <div className='sub'>120 saved</div>
                  <div className='grid'>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='cap'>Mirissa at sunrise</span>
                      <span className='bk'>
                        <svg viewBox='0 0 24 24'>
                          <path d='M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z' />
                        </svg>
                      </span>
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1537956965359-7573183d1f57?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='cap'>Hidden cove, south</span>
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='cap'>the Ella train</span>
                      <span className='bk'>
                        <svg viewBox='0 0 24 24'>
                          <path d='M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z' />
                        </svg>
                      </span>
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='bk'>
                        <svg viewBox='0 0 24 24'>
                          <path d='M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z' />
                        </svg>
                      </span>
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='cap'>Sunset deck</span>
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='bk'>
                        <svg viewBox='0 0 24 24'>
                          <path d='M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z' />
                        </svg>
                      </span>
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='cap'>Halong day-trip</span>
                    </div>
                    <div className='cell'>
                      <img
                        src='https://images.unsplash.com/photo-1530789253388-582c481c54b0?auto=format&fit=crop&w=240&q=70'
                        alt=''
                        loading='lazy'
                      />
                      <span className='cap'>Cappadocia at dawn</span>
                      <span className='bk'>
                        <svg viewBox='0 0 24 24'>
                          <path d='M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z' />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='painCap'>
              <div className='e'>The bookmarks</div>
              <div className='t'>120 saved. Zero plans.</div>
            </div>
          </div>

          <div className='painCol'>
            <div className='sph tr'>
              <div className='scr'>
                <div className='igdm'>
                  <div className='top'>
                    <div className='av'></div>
                    <div className='nm'>
                      rohan_t<small>Active now</small>
                    </div>
                    <div className='ic'>
                      <svg className='icn' viewBox='0 0 24 24'>
                        <path d='M15.6 10.8a11 11 0 0 0 4.6 4.6l1.5-1.5a1 1 0 0 1 1-.25 11 11 0 0 0 3.2.5' />
                        <path d='M3 5a2 2 0 0 1 2-2h2.5a1 1 0 0 1 1 .75c.15.9.4 1.8.7 2.6a1 1 0 0 1-.25 1L9 9' />
                      </svg>
                      <svg className='icn' viewBox='0 0 24 24'>
                        <rect x='2.5' y='6' width='13' height='12' rx='2' />
                        <path d='M15.5 10l6-3v10l-6-3' />
                      </svg>
                    </div>
                  </div>
                  <div className='body'>
                    <div className='txt rx'>you HAVE to see this one 😍</div>
                    <div className='reel rx'>
                      <div className='img'>
                        <img
                          src='https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?auto=format&fit=crop&w=300&q=75'
                          alt=''
                          loading='lazy'
                        />
                      </div>
                      <div className='play'>
                        <svg viewBox='0 0 24 24'>
                          <path d='M8 5v14l11-7z' />
                        </svg>
                      </div>
                      <div className='rl'>Reel</div>
                    </div>
                    <div className='cap rx'>Mirissa at sunrise</div>
                    <div className='txt tx'>omg saving this 😩</div>
                    <div className='reel tx'>
                      <div className='img'>
                        <img
                          src='https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?auto=format&fit=crop&w=300&q=75'
                          alt=''
                          loading='lazy'
                        />
                      </div>
                      <div className='play'>
                        <svg viewBox='0 0 24 24'>
                          <path d='M8 5v14l11-7z' />
                        </svg>
                      </div>
                      <div className='rl'>Reel</div>
                    </div>
                    <div className='cap tx'>the Ella train</div>
                  </div>
                </div>
              </div>
            </div>
            <div className='painCap'>
              <div className='e'>The forwarded reels</div>
              <div className='t'>Lost between memes.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== INTRODUCING THE COMPANION ============== */}
      <section className='actsec'>
        <div className='shead'>
          <h2 className='rv-wipe'>Meet Compass. Your travel concierge.</h2>
          <p className='rv'>
            Add it to the group, send it a reel, tag it under a video. It remembers all of it.
          </p>
        </div>
        <div className='phones rv-stagger'>
          {/* WhatsApp with Compass */}
          <div className='sph tl'>
            <div className='scr'>
              <div className='wa'>
                <div className='top'>
                  <svg className='icn bk' viewBox='0 0 24 24'>
                    <path d='M15 18l-6-6 6-6' />
                  </svg>
                  <div className='av'>SL</div>
                  <div className='nm'>
                    Sri Lanka 2026<small>6 members, Compass added</small>
                  </div>
                </div>
                <div className='body'>
                  <div className='msg rx'>
                    <span className='who' style={{ color: '#d9821a' }}>
                      Karan
                    </span>
                    someone just MAKE a plan 🙏
                  </div>
                  <div className='cmsg cReveal'>
                    <div className='who'>
                      <span className='mk'></span>Compass
                    </div>
                    <div className='t'>
                      I've read the 40 links and 12 reels you all shared. Mirissa, Galle and Ella
                      keep coming up. Want a 4-night south-coast plan for the group?
                    </div>
                  </div>
                  <div className='msg rx'>
                    <span className='who' style={{ color: '#d9527e' }}>
                      Priya
                    </span>
                    YES finally 🙌
                  </div>
                  <div className='msg tx'>
                    do it<span className='tm'>9:51</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Instagram bot */}
          <div className='sph'>
            <div className='scr'>
              <div className='igbot'>
                <div className='top'>
                  <div className='av'>
                    <svg className='icn' viewBox='0 0 24 24'>
                      <circle cx='12' cy='12' r='9' />
                      <path d='M15 9l-2 6-6 2 2-6z' />
                    </svg>
                  </div>
                  <div className='nm'>
                    hey.compass<small>Business chat</small>
                  </div>
                  <div className='ic'>
                    <svg className='icn' viewBox='0 0 24 24'>
                      <path d='M15.6 10.8a11 11 0 0 0 4.6 4.6l1.5-1.5a1 1 0 0 1 1-.25 11 11 0 0 0 3.2.5' />
                      <path d='M3 5a2 2 0 0 1 2-2h2.5a1 1 0 0 1 1 .75c.15.9.4 1.8.7 2.6a1 1 0 0 1-.25 1L9 9' />
                    </svg>
                  </div>
                </div>
                <div className='body'>
                  <div className='reel'>
                    <div className='img'>
                      <img
                        src='https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?auto=format&fit=crop&w=300&q=75'
                        alt=''
                        loading='lazy'
                      />
                    </div>
                    <div className='ov'>
                      <div className='l1'>6 DAYS ITINERARY</div>
                      <div className='l2'>SRI LANKA</div>
                    </div>
                  </div>
                  <div className='txt rx'>On it.</div>
                  <div className='itin'>
                    <div className='h'>🇱🇰 Sri Lanka · 6-day itinerary · 15 spots</div>
                    <div className='sec'>
                      📍 <b>Southern Province</b>
                      <br />🏛 Galle Fort · 🏨 Radisson · 🍴 Isle of Gelato
                    </div>
                    <div className='sec'>
                      📍 <b>Western Province</b>
                      <br />🚊 Marine Drive <span className='more'>+ 9 more</span>
                    </div>
                    <div className='sum'>15 stops across 6 days. Bookmark-worthy.</div>
                  </div>
                  <div className='qa'>
                    <div className='lbl'>Quick actions 👇</div>
                    <span className='btn'>📁 My Sri Lanka</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* YouTube Shorts comment */}
          <div className='sph tr'>
            <div className='scr'>
              <div className='yt'>
                <div className='vid'>
                  <img
                    src='https://images.unsplash.com/photo-1537956965359-7573183d1f57?auto=format&fit=crop&w=400&q=75'
                    alt=''
                    loading='lazy'
                  />
                  <div className='g'></div>
                  <div className='play'>
                    <svg viewBox='0 0 24 24'>
                      <circle cx='12' cy='12' r='11' fill='rgba(0,0,0,0.45)' />
                      <path d='M10 8.5v7l5.5-3.5z' fill='#fff' />
                    </svg>
                  </div>
                  <div className='rail'>
                    <div className='ri'>
                      <svg className='icn' viewBox='0 0 24 24'>
                        <path d='M7 10v11M7 10l4-7a2 2 0 0 1 3 2l-1 5h5a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 16.8 21H7' />
                      </svg>
                      <span>4.2K</span>
                    </div>
                    <div className='ri'>
                      <svg className='icn' viewBox='0 0 24 24'>
                        <path d='M21 11.5a8.5 8.5 0 0 1-12.2 7.6L3 21l1.9-5.8A8.5 8.5 0 1 1 21 11.5z' />
                      </svg>
                      <span>312</span>
                    </div>
                    <div className='ri'>
                      <svg className='icn' viewBox='0 0 24 24'>
                        <path d='M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2v13' />
                      </svg>
                      <span>Share</span>
                    </div>
                  </div>
                  <div className='meta'>
                    <div className='ch'>@ceylon.reels</div>
                    <div className='ti'>Hidden cove, south coast 🌊 #srilanka</div>
                  </div>
                </div>
                <div className='sheet'>
                  <div className='shd'>
                    Comments<span>312</span>
                  </div>
                  <div className='cmt dim'>
                    <div className='av'></div>
                    <div className='bd'>
                      <div className='u'>@rhea_t · 2d</div>
                      <div className='t'>where IS this 😍</div>
                    </div>
                  </div>
                  <div className='cgrp'>
                    <div className='cmt mine'>
                      <div className='av'></div>
                      <div className='bd'>
                        <div className='u'>@anshul · now</div>
                        <div className='t'>
                          <span className='men'>@compass</span> add this to my Sri Lanka board
                        </div>
                      </div>
                    </div>
                    <div className='cmt reply cReveal'>
                      <div className='av c'></div>
                      <div className='bd'>
                        <div className='u'>Compass · now</div>
                        <div className='t'>
                          Saved to your Sri Lanka board <span className='ck'>✓</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='cmt dim'>
                    <div className='av'></div>
                    <div className='bd'>
                      <div className='u'>@karan.j · 1d</div>
                      <div className='t'>the water though 🔥</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='ctablock rv'>
          <p className='ctaline'>Start with a travel reel you want to keep.</p>
          <div className='stores'>
            <DownloadCta placement='meet' />
          </div>
        </div>
      </section>

      {/* ============== WHAT IS COMPASS (3 screens) ============== */}
      <section className='threeup-sec'>
        <div className='shead'>
          <h2 className='rv-wipe'>
            Your saved places. Your kind of trip.
          </h2>
          <p className='rv'>
            Start with a reel, keep the places, and build a plan you can make your own.
          </p>
        </div>
        <div className='threeup rv-stagger'>
          <div className='col3'>
            <div className='dphone'>
              <div className='fHome'>
                <div className='hd'>
                  <div className='wm'>Compass</div>
                  <div className='pf'>
                    <svg className='icn' viewBox='0 0 24 24'>
                      <circle cx='12' cy='8' r='4' />
                      <path d='M4 21a8 8 0 0 1 16 0' />
                    </svg>
                  </div>
                </div>
                <div className='greet'>
                  <div className='e'>Thursday &middot; Afternoon</div>
                  <div className='h'>
                    Good afternoon, <span className='n'>Anshul</span>.
                  </div>
                  <div className='s'>Your Sri Lanka board just hit 120 saves.</div>
                </div>
                <div className='row'>
                  <div className='tcard'>
                    <div className='cov'>
                      <img
                        src='https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=380&q=75'
                        alt=''
                        loading='lazy'
                      />
                      <span className='pill'>Planned &middot; Vietnam</span>
                    </div>
                    <div className='bd'>
                      <div className='t'>Autumn Gold Journey</div>
                      <div className='dt'>1 - 10 Oct</div>
                      <div className='op'>Open trip &rarr;</div>
                    </div>
                  </div>
                  <div className='tcard'>
                    <div className='cov'>
                      <img
                        src='https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?auto=format&fit=crop&w=380&q=75'
                        alt=''
                        loading='lazy'
                      />
                      <span className='pill'>Saved itinerary</span>
                    </div>
                    <div className='bd'>
                      <div className='t'>12-day plan</div>
                      <div className='dt'>from @srilankatours</div>
                      <div className='op'>Open plan &rarr;</div>
                    </div>
                  </div>
                </div>
                <div className='colrow'>Your collections &middot; 16</div>
                <div className='colcard'>
                  <img
                    src='https://images.unsplash.com/photo-1537956965359-7573183d1f57?auto=format&fit=crop&w=560&q=80'
                    alt=''
                    loading='lazy'
                  />
                  <div className='g'></div>
                  <div className='star'>
                    <svg viewBox='0 0 24 24'>
                      <path d='M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 6 20.4l1.4-6.8L2.2 9l6.9-.7z' />
                    </svg>
                    Most saved
                  </div>
                  <div className='cap'>
                    <div className='ce'>Most saved &middot; Sri Lanka</div>
                    <div className='ch'>Sri Lanka</div>
                    <div className='cs'>135 places &middot; 13 cities</div>
                  </div>
                </div>
                <div className='foot'>
                  <div className='ipill'>
                    <div className='ndl'>
                      <svg viewBox='0 0 24 24'>
                        <circle cx='12' cy='12' r='9' />
                        <path d='M15 9l-2 6-6 2 2-6z' />
                      </svg>
                    </div>
                    <div className='itx'>Save a reel - paste it here.</div>
                    <svg className='icn mic' viewBox='0 0 24 24'>
                      <rect x='9' y='3' width='6' height='13' rx='3' />
                      <path d='M5 12a7 7 0 0 0 14 0M12 19v3' />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className='cap'>
              <div className='e'>Home</div>
              <h3>Your trips and collections, one tap away.</h3>
              <p>The reels you saved become collections. The trips you planned wait at the top.</p>
            </div>
          </div>

          <div className='col3'>
            <div className='dphone'>
              <div className='fChat'>
                <div className='cqx'>Compass - your Sri Lanka board</div>
                <div className='ureq'>Plan a trip from my Sri Lanka board</div>
                <div className='clead'>
                  You've saved <em>120 things</em> to Sri Lanka. Here's what you keep coming back to.
                </div>
                <div className='crail'>
                  <div className='ccard'>
                    <div className='ph'>
                      <img
                        src='https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?auto=format&fit=crop&w=320&q=80'
                        alt=''
                        loading='lazy'
                      />
                    </div>
                    <div className='ce'>Beach</div>
                    <div className='cn'>Mirissa</div>
                  </div>
                  <div className='ccard'>
                    <div className='ph'>
                      <img
                        src='https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=320&q=80'
                        alt=''
                        loading='lazy'
                      />
                    </div>
                    <div className='ce'>Stay</div>
                    <div className='cn'>Cliff villa</div>
                  </div>
                  <div className='ccard'>
                    <div className='ph'>
                      <img
                        src='https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?auto=format&fit=crop&w=320&q=80'
                        alt=''
                        loading='lazy'
                      />
                    </div>
                    <div className='ce'>To do</div>
                    <div className='cn'>Nine Arch</div>
                  </div>
                </div>
                <div className='cnudge cReveal'>
                  <div className='e'>A suggestion</div>
                  <div className='t'>Want me to draft a long weekend in the south?</div>
                  <div className='go'>
                    Draft south Sri Lanka{' '}
                    <svg viewBox='0 0 24 24'>
                      <path d='M5 12h14M13 6l6 6-6 6'></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className='cap'>
              <div className='e'>The companion</div>
              <h3>Ask for the trip. It knows your taste.</h3>
              <p>
                One chat, no search bar. It surfaces what you keep coming back to, then offers to
                draft the weekend you've been circling.
              </p>
            </div>
          </div>

          <div className='col3'>
            <div className='dphone'>
              <div className='fCredit'>
                <div className='e'>From your saved places</div>
                <h3>A trip that starts with you.</h3>
                <div className='emi'>
                  <div className='r'><span className='k'>Places</span><span className='val'>From the reels you keep</span></div>
                  <div className='r'><span className='k'>Days</span><span className='val'>An editable plan</span></div>
                  <div className='r'><span className='k'>People</span><span className='val'>Share with your travel crew</span></div>
                </div>
                <div className='nudge'>
                  <div className='e2'>Your next step</div>
                  <div className='t'>Start with one travel reel. Keep the places you want to come back to.</div>
                </div>
              </div>
            </div>
            <div className='cap'>
              <div className='e'>Your next trip</div>
              <h3>From saved to planned.</h3>
              <p>
                Bring your saved places into an editable itinerary. Keep the ideas that matter and make the trip your own.
              </p>
            </div>
          </div>
        </div>
        <div className='ctablock rv'>
          <p className='ctaline'>Get the app. Save your first travel reel.</p>
          <div className='stores'>
            <DownloadCta placement='payin3' />
          </div>
        </div>
      </section>

      {/* ============== CONCIERGE ============== */}
      <section className='benefits'>
        <div className='shead'>
          <h2 className='rv-wipe'>Keep your travel ideas close.</h2>
          <p className='rv'>From the first saved reel to the places on your next trip.</p>
        </div>
        <div className='grid rv-stagger'>
          <div className='b'>
            <div className='ic'>
              <img
                src='https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=160&q=75'
                alt=''
                loading='lazy'
              />
            </div>
            <h4>A plan you can edit</h4>
            <p>
              Move places between days and adjust your itinerary as your plans take shape.
            </p>
          </div>
          <div className='b'>
            <div className='ic'>
              <img
                src='https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=160&q=75'
                alt=''
                loading='lazy'
              />
            </div>
            <h4>Keep the original reel</h4>
            <p>Return to the inspiration behind a saved place when you need a second look.</p>
          </div>
          <div className='b'>
            <div className='ic'>
              <img
                src='https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=160&q=75'
                alt=''
                loading='lazy'
              />
            </div>
            <h4>Picked for you</h4>
            <p>It knows your taste, so what it suggests on the ground actually feels like you.</p>
          </div>
          <div className='b'>
            <div className='ic'>
              <img
                src='https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=160&q=75'
                alt=''
                loading='lazy'
              />
            </div>
            <h4>Plan together</h4>
            <p>Share a trip with the people coming along and bring your ideas together.</p>
          </div>
          <div className='b'>
            <div className='ic'>
              <img
                src='https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=160&q=75'
                alt=''
                loading='lazy'
              />
            </div>
            <h4>Find your next place</h4>
            <p>Browse your saved collections when you know where you are going.</p>
          </div>
          <div className='b'>
            <div className='ic'>
              <img
                src='https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=160&q=75'
                alt=''
                loading='lazy'
              />
            </div>
            <h4>Room for the details</h4>
            <p>Keep the places and plans for your trip in one app.</p>
          </div>
        </div>
      </section>

      {/* ============== WEDGE (closing) ============== */}
      <section className='wedge'>
        <img
          src='https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1600&q=80'
          alt=''
          loading='lazy'
        />
        <div className='grad'></div>
        <div className='stage'>
          <h2>
            The destination was never the point. The{' '}
            <span className='brass hRot'>
              <span className='word' data-rot='exp'>
                experience
              </span>
            </span>{' '}
            was.
          </h2>
          <div className='ctablock rv'>
            <p className='ctaline'>Start with the reels you have already saved.</p>
            <div className='stores'>
              <DownloadCta placement='wedge' />
            </div>
          </div>
        </div>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className='foot2'>
        <div className='row'>
          <div className='wordmark-d'>Compass</div>
          <div className='meta'>
            <span>(c) 2026 Compass, India</span>
            <a href='/privacy.html'>Privacy</a>
            <a href='/terms.html'>Terms</a>
          </div>
        </div>
      </footer>

      <StickyDownloadBar />
    </div>
  )
}
